import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NumberInput from '../../theme/inputs/NumberInput';
import Shbutton from '../../theme/buttons/Shbutton';
import Text from '../../theme/text/Text';
import { useTranslation } from "react-i18next";
import { useUpdateSettingForRelationMutation, useUpdateSettingMutation } from '../../../newapi/global/generalApiSlice';
import { tokens } from '../../../theme';
import { showToastMessage, showToastMessageRequestError } from '../../../utils/toasts';

// variants
//    1: default has both a title and subtitle
//    2: has no subtitle ans text is light
//
// manualSave: replaces the debounced autosave with an explicit Save button that
//   only appears once the value differs from what is saved, making persistence
//   state visible. Combine with `required` to disable saving while the field is empty.
// hideSaveButton: keeps manualSave's "never autosave" behaviour but leaves out the button,
//   for screens that collect several fields behind one save action of their own. Such a
//   screen tracks the value through `onChange(value, setting)` and persists it itself,
//   which is what useManualSettingsSave does.
const NumberSetting = (props) => {
  const { title = true, subtitle = true, variant = 1, disabled = false, required = false, manualSave = false, hideSaveButton = false } = props;
  const showSaveButton = manualSave && !hideSaveButton;
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [updateSetting] = useUpdateSettingMutation();
  const [updateSettingForRelation] = useUpdateSettingForRelationMutation();

  const [val, setVal] = useState();
  const [setting, setSetting] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const debounceRef = useRef(null);

  // A required setting may never be persisted empty. Used to skip saving while
  // the field is blank and to revert to the last saved value on blur.
  const isEmptyValue = (value) => value === '' || value === null || typeof value === "undefined";

  const debouncedUpdateSetting = useCallback(async (settingData, newValue) => {
    try {
      if(props.relationId){
        await updateSettingForRelation({
          key: settingData.key,
          value: newValue,
          relationId: props.relationId
        });
      } else {
        await updateSetting({
          key: settingData.key,
          value: newValue,
          warehouseId: settingData.warehouseId,
        });
      }
    } catch (error) {
      console.error('Failed to update number setting:', error);
    }
  }, [updateSetting, updateSettingForRelation, props.relationId]);

  const handleChange = useCallback((value) => {
    setVal(value);

    if (props.onChange) {
      props.onChange(value, props.relationId ? { ...setting, relationId: props.relationId } : setting);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Manual-save fields persist only when the Save button is clicked.
    if (manualSave) {
      return;
    }

    // Never autosave a blank value for a required setting. The previous value
    // stays persisted until a valid one is entered; blur restores the display.
    if (required && isEmptyValue(value)) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      if (setting) {
        debouncedUpdateSetting(setting, value);
      }
    }, 500);
  }, [setting, debouncedUpdateSetting, props.onChange, props.relationId, required, manualSave]);

  const handleBlur = useCallback(() => {
    // On autosave, a required field left empty reverts to the last saved value so
    // the UI never shows a blank that was never persisted. Manual-save keeps the
    // typed value visible so the user can correct it and press Save.
    if (required && !manualSave && isEmptyValue(val)) {
      setVal(setting?.value);
    }
  }, [required, manualSave, val, setting]);

  const handleSave = useCallback(async () => {
    if (!setting || isSaving || (required && isEmptyValue(val))) {
      return;
    }
    setIsSaving(true);
    try {
      if (props.relationId) {
        await updateSettingForRelation({
          key: setting.key,
          value: val,
          relationId: props.relationId,
        }).unwrap();
      } else {
        await updateSetting({
          key: setting.key,
          value: val,
          warehouseId: setting.warehouseId,
        }).unwrap();
      }
      // Advance the saved baseline so the dirty state clears immediately, before
      // the settings query refetches and confirms the new value.
      setSetting((prev) => ({ ...prev, value: val }));
      showToastMessage("success", t("update_successfull"));
    } catch (error) {
      showToastMessageRequestError(t("update_failed"), t, error);
    } finally {
      setIsSaving(false);
    }
  }, [setting, val, required, isSaving, props.relationId, updateSetting, updateSettingForRelation, t]);

  // useEffect(() => {
  //   if(props.skey && typeof props.settings !== "undefined"){
  //       let xsetting = props.settings.find((setting) => setting.key === props.skey);
  //       if (!xsetting) {
  //           xsetting = { key: props.skey, value: 0, warehouseId: -1 };
  //       }
  //       setSetting(xsetting);
  //   }
  // }, [props.settings, props.skey]);

  useEffect(() => {
    if(props.skey && typeof props.settings !== "undefined"){
        let xsetting = props.settings.find((setting) => setting.key === props.skey);
        if (!xsetting) {
          if(props.relationId){
            xsetting = { key: props.skey, value: "unset", relationId: props.relationId };
          }else{
            xsetting = { key: props.skey, value: false, warehouseId: -1 };
          }
        }
        setSetting(xsetting);
        let xval = disabled ? false : xsetting.value;
        setVal(xval);
    }
  }, [props.settings, props.skey, props.relationId, disabled]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: 1,
      }}
    >
      <Box
        sx={{
          display:"none",
          "@media screen and (max-width: 47.9375em)": {
            display:"block"
          },
        }}
      >
        <Box
          sx={{
            ml:"-12px",
            mr:"-12px",
            pl:2,
            pr:1,
            display:"flex",
            py:1,
            borderRadius:7,
            gap:2,
            alignItems:"center",
            justifyContent:"space-between",
            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
            
          }}
        >
          {title && <Text semibold={props.variant !== 2} size={variant === 2 ?? "fs13"} >{t(`${setting?.key}_title`)}</Text>}
          
          <Box
            sx={{
              flex: 1,
              maxWidth: props.maxWidth ?? 150,
              marginLeft: "auto",
              backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
              borderRadius: 4,
              display: "flex",
              height: 32,
              ...props.sx
            }}
          >
            <NumberInput
              btnSize={"24"}
              value={val}
              change={(e) => handleChange(e)}
              blur={handleBlur}
              inputSX={props.inputSX}
              btnSx={props.btnSx}
            />
            {props.extra && props.extra}
          </Box>
          {showSaveButton && (
            <Shbutton
              size={"32"}
              color={theme.palette.mode === "dark" ? "blue" : "primary"}
              variant="contained"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={required && isEmptyValue(val)}
              sx={{ flexShrink: 0 }}
            >
              {t('save')}
            </Shbutton>
          )}

        </Box>
        {subtitle && 
          <Text 
            light
            sx={{
              pt:1,
            }}
          >
            {t(`${setting?.key}_subtitle`)}
          </Text>
        }

      </Box>

      <Box
        sx={{
          width: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "@media screen and (max-width: 47.9375em)": {
            display:"none"
          },
        }}
      >
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
          }}
        >
          {title && <Text size={variant === 2 ?? "fs13"} semibold={props.variant !== 2} >{t(`${setting?.key}_title`)}</Text>}
          {subtitle && variant !== 2 && <Text light>{t(`${setting?.key}_subtitle`)}</Text>}
        </Box>

        <Box
          sx={{
            flex: 1,
            maxWidth: props.maxWidth ?? 150,
            marginLeft: "auto",
            backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
            borderRadius: 4,
            display: "flex",
            height: 32,
            ...props.sx,
          }}
        >
          <NumberInput
            btnSize={"24"}
            value={val}
            change={(e) => handleChange(e)}
            blur={handleBlur}
            inputSX={props.inputSX}
            btnSx={props.btnSx}
          />
          {props.extra && props.extra}
        </Box>
        {showSaveButton && (
            <Shbutton
            size={"32"}
              color={theme.palette.mode === "dark" ? "blue" : "primary"}
              variant="contained"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={required && isEmptyValue(val)}
              sx={{ 
                flexShrink: 0,
                borderTopLeftRadius:0,
                borderBottomLeftRadius:0,
                
              }}
            >
              {t('save')}
            </Shbutton>
          )}
      </Box>

      {required && isEmptyValue(val) && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            height: 40,
            borderRadius: 5,
            background: colors.red[100],
            border: `1px solid ${colors.red[400]}`,
            color: colors.red[400],
          }}
        >
          <WarningAmberIcon />
          <Text bold>{t('field_required')}</Text>
        </Box>
      )}
    </Box>
  );
};

export default NumberSetting;